/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
import { Rectangle, Texture, utils } from 'pixi.js-legacy';
import { Camera } from '@appCanvasCommon/core/Camera';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { Grid } from '@appCanvasCommon/canvas-grid/Grid';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { PLAN_CONSTANTS, ProjectMember } from '@touchplan/common';
import { CrewSizeQuery } from 'src/app/plain-javascript/crew-sizes/CrewSizeQuery';
import { Role } from '@touchplan/common';
import { SpritePool } from 'src/app/app-data/models/data-types/GenericPool';
import { LaborCount } from 'src/app/app-data/models/LaborCount';
import { map, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { dateCache } from '@touchplan/common';
import { throttleEvent } from 'utils/basic-utils';
import { convertHexStringToNumber } from 'utils/color-utils';
import * as ss from 'simple-statistics';
import { ICanvasApplicationInitOptions } from '@appCanvasCommon/interfaces/canvas-renderer.interface';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { TICKET_TEXTURE_OPTIONS } from '@appPlanApplication/tickets/TICKET_TEXTURE_OPTIONS';
import { NumberUtils } from 'src/app/utils/number-utils';
import { ExportCapture, ICapturedTile, ITileOffset } from '@appCanvasCommon/export/ExportCapture';
import { ICrewSizeApplicationDependencies } from '../../app-data/interfaces/crew-size-dependencies';
import { IActiveColumn } from '../../modules/plan/services/active-column.service';
import { RenderDependencies } from '@appPlanApplication/RenderDependencies';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';

/**
 * The layers for the labor count application
 */
export enum CREW_SIZE_LAYERS {
	UI_BACKGROUND = 'uiBackgroundLayer',
	WORLD = 'worldLayer',
	BARS = 'barsLayer',
	GRID = 'gridLayer',
	UI = 'uiLayer',
	UI_X_AXIS = 'uiXAxisLayer',
	UI_Y_AXIS = 'uiYAxisLayer',
}

export const CREW_SIZE_SETTINGS = {
	HEIGHT: 122,
	BACKGROUND_COLOR: 0xe3e3e3,
	BACKGROUND_ALPHA: 0.6,
	AXES_ALPHA: 0.8,
	TIMELINE_Y_AXIS_WIDTH: PLAN_CONSTANTS.SIDEBAR_WIDTH + 50,
	TIMELINE_Y_AXIS_BACKGROUND_COLOR: 0xdfdfdf,
	TIMELINE_X_AXIS_HEIGHT: 20,
	TIMELINE_X_AXIS_BACKGROUND_COLOR: 0xc7c7c7,
	TIMELINE_X_AXIS_SPACING: 15,
	TIMELINE_X_AXIS_SPACING_TOP: 37,
	GRID_LODS: [2, 3],
	GRID_MIN_SCALE: 0.001,
	GRID_MAX_SCALE: 500,
	GRID_SPACING: 20,
	GRID_COLOR: 0xbdbdbd,
	BAR_DRAW_Y_OFFSET: 35,
	SCALE_FOR_DAYS: 0.23,
	Y_AXIS_X_OFFSET: 120,
	Y_AXIS_Y_OFFSET: -8,
	BAR_SPACING: 2, // The padding for the bars
	MAX_ZOOM_LEVEL: 0.035, // This is the zoom level when the "Zoom In" message appears at
};

export interface ICombinedLabor {
	id: string;
	plannedAndActual: number;
	color: number;
}

export interface ILaborDraw {
	index: number;
	labor: Map<string, ICombinedLabor>;
	startX: number;
	finishX: number;
	width: number;
	day: string;
	plannedAndActual: number;
	projectPlannedAndLastActual: number;
}

/**
 * Convert the hex string to a number once, then store it..
 */
export interface ILaborColorCache {
	[key: string]: number;
}

/**
 * The main canvas application for the LaborCount component
 */
export class CrewSizeApplication extends AbstractApplication {
	public active = false;
	public backgroundGraphics: FancyGraphics = new FancyGraphics();
	public graphics: FancyGraphics = new FancyGraphics();
	public uiGraphics: FancyGraphics = new FancyGraphics();
	public width: number;
	public height: number;
	public crewSizeIcon: FancySprite;
	public drawDays = false;
	public grid: Grid;
	public query: CrewSizeQuery;

	// Layer references
	public uiBackgroundLayer: FancyContainer;
	public worldLayer: FancyContainer;
	public barsLayer: FancyContainer;
	public gridLayer: FancyContainer;
	public uiLayer: FancyContainer;
	public xAxesLayer: FancyContainer;
	public yAxesLayer: FancyContainer;
	public projectText: HighResolutionText;

	private _destroyList: any[] = [];
	public _cachedDays;
	private _lastDrawDays: boolean = null;
	private _cachedRoles: Map<string, Role>;
	private _projectLabor: Map<string, LaborCount>;
	private _visibleLabor: ILaborDraw[];
	private _lastStart: string;
	private _lastEnd: string;
	// For filtering
	private _filterBy: any = []; //LABOR_COUNT_FILTER_VALUES.ALL_ROLES;
	private _myRoleId = '';

	private _planCountTextPool: SpritePool;
	private _projectCountTextPool: SpritePool;

	private _maxText: HighResolutionText = new HighResolutionText(
		'',
		new FancyTextStyle({ fontSize: 14, fill: 'black', resolution: 2 })
	);
	private _midText: HighResolutionText = new HighResolutionText(
		'',
		new FancyTextStyle({ fontSize: 14, fill: 'black', resolution: 2 })
	);
	private _minBar: FancySprite = new FancySprite((<any>Texture).TASK_TEXTURE);

	private _projectMember!: ProjectMember | null;
	/**
	 * A special flag which prevents drawing of horizontal elements when set.
	 * The intention for this flag is to allow us to customize elements that are drawn so that we can stitch them together
	 * When true, this disables the following elements:
	 * 	- Disables MAX and AVERAGE labels
	 * 	- Prevents drawing of UI cover along left edge
	 * 	- Prevents drawing of manpower icon
	 * 	- Prevents drawing of Project text
	 * 	- Extends X-axis divider line to left edge of the screen
	 * @private
	 */
	private _shouldTile: boolean = false;

	/**
	 * Instance of the exporter for the crew sizes
	 */
	private _export!: ExportCapture | null;

	/**
	 * Helper getter to retrieve all of the captured tiles from the crew size application
	 */
	public get capturedTiles(): ICapturedTile[] {
		return this._export?.tiles || [];
	}

	public get exporter(): ExportCapture | null {
		return this._export;
	}

	constructor(
		private _dependencies: ICrewSizeApplicationDependencies,
		initOptions: ICanvasApplicationInitOptions
	) {
		super(_dependencies, initOptions);
		RenderDependencies.loaded$.pipe(takeUntil(this.shutdownSubject$)).subscribe((_) => this.setup());
	}

	private setup(): void {
		// Init boilerplate

		// Set inital dimensions
		this.width = this.renderer.width;
		this.height = this.renderer.height;

		// Create static text textures
		this.projectText = new HighResolutionText(
			'Project:',
			new FancyTextStyle({ fontFamily: 'Roboto', fill: 'black', fontSize: 12, resolution: 2 })
		);

		this.projectText.x = PLAN_CONSTANTS.SIDEBAR_WIDTH - CREW_SIZE_SETTINGS.Y_AXIS_Y_OFFSET;
		this.projectText.y = CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT / 2 - this.projectText.height / 2;

		// Create layers
		this.uiBackgroundLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.UI_BACKGROUND, this.stage);
		this.worldLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.WORLD, this.stage);
		this.barsLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.BARS, this.stage);
		this.gridLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.GRID, this.stage);
		this.uiLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.UI, this.stage);
		this.xAxesLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.UI_X_AXIS, this.stage);
		this.yAxesLayer = this.layerManager.addLayer(CREW_SIZE_LAYERS.UI_Y_AXIS, this.stage);

		// Position Grid Container
		this.gridLayer.y = CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING_TOP;

		// Position axes containers
		this.xAxesLayer.x = 0;
		this.xAxesLayer.y = this.height / this.renderer.resolution - CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT;

		// Create the camera and assign it to our world
		this.camera = new Camera(
			this.worldLayer,
			this.stage,
			this.tweenManager,
			0,
			0,
			this.viewport.width,
			this.viewport.height
		);
		// Camera scale is set to 0 by default to fix a bug for plan camera...so just set it to 1 here
		this.camera.scale.x = this.camera.scale.y = 1;

		// Create our grid
		this.grid = new Grid(
			this.width / this.renderer.resolution,
			this.height / this.renderer.resolution -
				CREW_SIZE_SETTINGS.BAR_DRAW_Y_OFFSET -
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING_TOP,
			this.gridLayer
		);

		this.grid.setScaleV(
			CREW_SIZE_SETTINGS.GRID_LODS,
			CREW_SIZE_SETTINGS.GRID_MIN_SCALE,
			CREW_SIZE_SETTINGS.GRID_MAX_SCALE,
			CREW_SIZE_SETTINGS.GRID_SPACING,
			CREW_SIZE_SETTINGS.GRID_SPACING
		);

		// Crew Size Icon
		this.crewSizeIcon = new FancySprite(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CREW_ICON_NAME]);
		this.crewSizeIcon.width = 14;
		this.crewSizeIcon.height = 10;
		this.crewSizeIcon.active = true;
		this.crewSizeIcon.visible = true;
		this.crewSizeIcon.tint = 0x000000;

		// Add stuff to layers
		this.layerManager.getLayer(CREW_SIZE_LAYERS.UI_BACKGROUND).addChild(this.backgroundGraphics);
		this.layerManager.getLayer(CREW_SIZE_LAYERS.BARS).addChild(this.graphics, this._minBar as any); // This will eventually draw our bars
		this.layerManager.getLayer(CREW_SIZE_LAYERS.UI).addChild(this.uiGraphics); // This draws our timeline background
		this.layerManager.getLayer(CREW_SIZE_LAYERS.UI_X_AXIS).addChild(this.projectText); // Project Text
		this.layerManager
			.getLayer(CREW_SIZE_LAYERS.UI_Y_AXIS)
			.addChild(this._maxText, this._midText, this.crewSizeIcon as any); // Max / min text

		// Create pools

		// Plan Count text
		const textCreator = (): HighResolutionText => {
			const textObject: HighResolutionText = new HighResolutionText(
				'',
				new FancyTextStyle({ fontSize: 14, fill: 'black', resolution: 2 })
			);
			textObject.enableTrimText = false;
			return textObject;
		};

		// Project Count text
		const projectTextCreator = (): HighResolutionText => {
			const projectTextObject: HighResolutionText = new HighResolutionText(
				'',
				new FancyTextStyle({ fontSize: 14, fill: 'black', resolution: 2 })
			);
			projectTextObject.enableTrimText = false;
			return projectTextObject;
		};

		this._planCountTextPool = new SpritePool(textCreator, this.layerManager.getLayer(CREW_SIZE_LAYERS.UI));
		this._projectCountTextPool = new SpritePool(
			projectTextCreator,
			this.layerManager.getLayer(CREW_SIZE_LAYERS.UI_X_AXIS)
		);

		this._minBar.visible = true;
		this._minBar.active = true;

		this._minBar.width = this.width / this.renderer.resolution;
		this._minBar.height = 1;
		this._minBar.tint = CREW_SIZE_SETTINGS.GRID_COLOR;

		this._maxText.enableTrimText = false;
		this._midText.enableTrimText = false;
		this.projectText.enableTrimText = false;

		this._export = new ExportCapture(this);

		this.setupSubscribers();

		// Draw UI
		this.drawUI();

		this.run();

		this._destroyList.push(
			this._projectCountTextPool,
			this._planCountTextPool,
			this.projectText,
			this.query,
			this.grid,
			this.uiGraphics,
			this.graphics,
			this.backgroundGraphics,
			this._maxText,
			this._midText,
			this._minBar,
			this.crewSizeIcon
		);

		this._scaleDown();

		throttleEvent('resize', 'throttledResize');
		window.addEventListener('throttledResize', this.onResize);
		window.CrewSizeApplication = this;
	}
	public onResize = () => {
		const w: number = this._dependencies.cameraService.camera.view.width;
		this.renderer.resize(w, CREW_SIZE_SETTINGS.HEIGHT);
		this.camera.resize(w, CREW_SIZE_SETTINGS.HEIGHT);
		this.width = this.renderer.width;
		this.height = this.renderer.height;
		this._minBar.width = this.width;
		this._scaleDown();
		this.drawUI();
		this.run();
	};

	public setupSubscribers() {
		// Labor Query
		this.query = new CrewSizeQuery();

		// Subscribe to labor count data
		this._dependencies.laborCounts.data.list$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((laborCount: Map<string, LaborCount>) => {
				this.query.setData(laborCount);
				if (this._cachedDays) {
					this.update(this._cachedDays, true);
				}
			});

		// Subscribe to 'authedProjectMember'
		this._dependencies.authedProjectMember$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((authedProjectMember) => {
				this._myRoleId = authedProjectMember?.view?.roleId ? authedProjectMember.view.roleId : '';
				if (this._cachedDays) {
					this.update(this._cachedDays, true);
				}
			});

		// Subscribe to project labor
		this._dependencies.projectLaborCounts.data.list$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((projectLabor: Map<string, LaborCount>) => {
				this._projectLabor = projectLabor;
				if (this._cachedDays) {
					this.update(this._cachedDays, true);
				}
			});

		// Subscribe to role list
		this._dependencies.rolesApi.data.list$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((roles: Map<string, Role>) => {
				this._cachedRoles = roles;
			});

		this._dependencies.authedProjectMember$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((projectMember: ProjectMember) => {
				this._projectMember = projectMember;
			});

		combineLatest([
			this._dependencies.cameraService.camera.changed$,
			this._dependencies.activeColumnService.visibleColumns$,
		])
			.pipe(
				takeUntil(this.shutdownSubject$),
				map(([changedEvent, activeColumns]: [ViewportChangedEvent, IActiveColumn[] | null]) => {
					this._cachedDays = activeColumns;
					this.camera.setPositionAndScale(changedEvent.view.x, changedEvent.view.y, changedEvent.scale.x);
					return activeColumns;
				})
			)
			.subscribe((visibleColumns: IActiveColumn[] | null) => {
				if (!visibleColumns) {
					this.clear();
				}
				this.update(visibleColumns, true);
			});
	}

	private isRoleActive(roleId: string): boolean {
		// MOC-3150
		if (!this._filterBy || !this._filterBy.length) {
			return true;
		}
		for (const idx in this._filterBy) {
			if (this._filterBy[idx].$id === roleId) {
				return true;
			}
		}

		return false;
	}

	public updateFilter(newFilter: any): void {
		this._filterBy = newFilter;
		if (this._cachedDays) {
			this.update(this._cachedDays, true);
		}
	}

	public override update(columns = this._cachedDays, force: boolean = false): void {
		this.camera.update();
		if (!columns || !columns.length || !this.active) {
			return;
		}

		const finish = columns[0].activeDay;
		const start = columns[columns.length - 1].activeDay;
		if (!force && start === this._lastStart && finish === this._lastEnd) {
			return;
		}

		this._visibleLabor = this.processLabor(columns);

		this.render(this._visibleLabor);

		this._lastStart = start;
		this._lastEnd = finish;
	}

	/**
	 * Special method for plan export that forces the crew sizes to update and re-render. This method exists so we don't
	 * need to do CrewSizeApplication.upda
	 */
	public forceUpdate(): void {
		this.update(this._cachedDays, true);
	}

	public processLabor(activeColumns: IActiveColumn[]): any {
		// TODO: Clean this up (Move it to a model maybe? Can also improve performance here by caching all the things
		const returnResult: any = [];
		let columnWidth: number = 0;
		let displayColumnWidth: number = 0;
		let totalDays: number = 0;
		const totalValues: number[] = [];
		const first: IActiveColumn = activeColumns[activeColumns.length - 1];
		const last: IActiveColumn = activeColumns[0];

		if (!this._myRoleId && this._projectMember) {
			this._myRoleId = this._projectMember.view.roleId as string;
		}

		// Only process if we have at least two active days
		if (last && first) {
			// Calculate the widths of our columns
			columnWidth = last.finishX - last.startX;
			displayColumnWidth = this.camera.realToScreenX(last.finishX) - this.camera.realToScreenX(last.startX);

			// Determine how many days we have in the viewport range
			totalDays = dateCache.diffDays(last.activeDay, first.activeDay);
			let x: number = first.finishX;
			let screenX: number = this.camera.realToScreenX(first.startX);

			// Now we generate the data
			for (let i = 0; i <= totalDays; i++) {
				// These are variable that change for each day. Store them in this scope
				const activeDay: string = this._dependencies.activeColumnService.getDateFromX(x);
				const labourCount: LaborCount = this.query.getDay(activeDay);
				const combinedlabor: Map<string, ICombinedLabor> = new Map();

				// This tracks the total plannedAndLastPlanned values for the day
				let plannedAndActual: number = 0;
				let projectPlannedAndLastActual: number = 0;

				// We have crew size data for this day. Generate an ILaborDraw
				if (labourCount) {
					// Crew Sizes data is a list of lists...So go deeper...
					for (const roleId in labourCount.view) {
						// This controls our filter.
						if (!this.isRoleActive(roleId)) {
							continue;
						}

						// Generate the color
						const role: Role = this._cachedRoles.get(roleId) as Role;
						const roleColor: string = role && role.view.color ? role.view.color : '#000000';
						const color: number = convertHexStringToNumber(roleColor);

						// I don't like this... Probably change.
						combinedlabor.set(roleId, {
							id: roleId,
							color,
							plannedAndActual: labourCount.view[roleId].plannedAndActual,
						});

						plannedAndActual += labourCount.view[roleId].plannedAndActual;
					}

					// If we have a value higher than 0, add it to our total value tracker. We then pull the min and max values from this list
					if (plannedAndActual > 0) {
						totalValues.push(plannedAndActual);
					}
				}

				// If we have project labor data, then we try to read it and add the value to our model
				if (this._projectLabor) {
					const projectLabor: LaborCount | undefined = this._projectLabor.get(activeDay);
					if (projectLabor) {
						for (const roleId in projectLabor.view) {
							// This controls our filter.
							if (!this.isRoleActive(roleId) || roleId === 'allRoles') {
								continue;
							}
							projectPlannedAndLastActual += projectLabor.view[roleId].plannedAndActual;
						}
					}
				}

				// Add to our draw list
				returnResult.push({
					day: activeDay,
					labor: combinedlabor,
					startX: screenX,
					finishX: screenX + displayColumnWidth,
					plannedAndActual,
					projectPlannedAndLastActual,
					width: displayColumnWidth,
				});
				x += columnWidth;
				screenX += displayColumnWidth;
			}
		}

		// Update our grid
		if (totalValues.length) {
			let max: number = ss.max(totalValues);
			max = max % 2 === 0 ? max : (max += 1);
			const min: number = max / 2;
			this.updateGrid(
				min,
				max,
				this.camera.realToScreenX(activeColumns[0].finishX) -
					this.camera.realToScreenX(activeColumns[0].startX)
			);
		} else {
			this.updateGrid(
				0,
				1,
				this.camera.realToScreenX(activeColumns[0].finishX) -
					this.camera.realToScreenX(activeColumns[0].startX)
			);
		}
		return returnResult;
	}

	private _scaleDown(): void {
		this.view.style.width = this.renderer.width / this.renderer.resolution + 'px';
		this.view.style.height = this.renderer.height / this.renderer.resolution + 'px';
	}

	public updateGrid(min: number, max: number, columnWidth?: number): void {
		// Re-draw the UI...if necessary
		if (this.drawDays !== this._lastDrawDays) {
			if (this.drawDays) {
				this.projectText.visible = true;
				this.crewSizeIcon.visible = true;
				this.height = this.renderer.height;
			} else {
				this.projectText.visible = false;
				this.crewSizeIcon.visible = false;
				this.height = this.renderer.height + CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT;
			}

			this.drawUI();
			this._lastDrawDays = this.drawDays;
		}

		this.grid.yMaxRange = max;
		this.grid.yMinRange = min;

		this._maxText.text = '' + NumberUtils.roundToTwo(this.grid.yMaxRange);
		this._midText.text = min === 0 ? '' : '' + NumberUtils.roundToTwo(this.grid.yMinRange);

		// Calculate the grid scale.
		const maxHeight =
			this.height / this.renderer.resolution -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING_TOP;

		this.grid.yAxisScale = maxHeight / max;

		// Need to do this after setting scale.
		const maxHeight2 =
			this.height / this.renderer.resolution -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING;

		// Now set positions
		this._minBar.y = maxHeight2 - this.grid.valueToPixelV(this.grid.yMinRange);

		this._maxText.x = CREW_SIZE_SETTINGS.Y_AXIS_X_OFFSET - this._maxText.width / 2;
		this._maxText.y = CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING_TOP + CREW_SIZE_SETTINGS.Y_AXIS_Y_OFFSET;

		this._midText.x = CREW_SIZE_SETTINGS.Y_AXIS_X_OFFSET - this._midText.width / 2;
		this._midText.y = this._minBar.y + CREW_SIZE_SETTINGS.Y_AXIS_Y_OFFSET;

		this.crewSizeIcon.x = CREW_SIZE_SETTINGS.Y_AXIS_X_OFFSET - this.crewSizeIcon.width / 2;
		this.crewSizeIcon.y =
			this.height -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING -
			this.crewSizeIcon.height / 2;
	}

	public render(visibleLabor: ILaborDraw[]): void {
		this.graphics.clear();
		this._planCountTextPool.reset();
		this._projectCountTextPool.reset();
		const maxHeight =
			this.height / this.renderer.resolution -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING;

		visibleLabor.forEach((laborItem: ILaborDraw) => {
			const x: number = laborItem.startX;
			const w: number = laborItem.width;
			let sumValue = 0;

			laborItem.labor.forEach((labor: ICombinedLabor) => {
				const y: number = this.grid.valueToPixelV(labor.plannedAndActual);
				this.graphics.beginFill(labor.color, 1);

				this.graphics.drawRect(
					x + CREW_SIZE_SETTINGS.BAR_SPACING,
					this.height / this.renderer.resolution - CREW_SIZE_SETTINGS.BAR_DRAW_Y_OFFSET - sumValue - y,
					w - CREW_SIZE_SETTINGS.BAR_SPACING * 2,
					y
				);

				this.graphics.endFill();
				sumValue += y;
			});

			if (laborItem.plannedAndActual && this.drawDays) {
				const thing: HighResolutionText = this._planCountTextPool.grab() as HighResolutionText;
				thing.text = '' + NumberUtils.roundToTwo(laborItem.plannedAndActual);
				thing.active = true;
				thing.x =
					x + CREW_SIZE_SETTINGS.BAR_SPACING + (w - CREW_SIZE_SETTINGS.BAR_SPACING * 2) / 2 - thing.width / 2;
				thing.y = maxHeight - sumValue - CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING;
				const visibilityOffset: number = this._shouldTile ? 0 : CREW_SIZE_SETTINGS.Y_AXIS_X_OFFSET + 20;
				if (thing.x <= visibilityOffset) {
					thing.visible = false;
				}
			}

			const newThingX: number =
				x + CREW_SIZE_SETTINGS.BAR_SPACING + (w - CREW_SIZE_SETTINGS.BAR_SPACING * 2) / 2;
			if (laborItem.projectPlannedAndLastActual && this.drawDays) {
				const thing: HighResolutionText = this._projectCountTextPool.grab() as HighResolutionText;
				thing.text = '' + NumberUtils.roundToTwo(laborItem.projectPlannedAndLastActual);
				thing.active = true;
				thing.x = newThingX - thing.width / 2;
				thing.y = 2;
				const visibilityOffset: number = this._shouldTile ? 0 : CREW_SIZE_SETTINGS.Y_AXIS_X_OFFSET + 20;
				if (thing.x <= visibilityOffset) {
					thing.visible = false;
				}
			}
		});

		this.run();
	}

	/**
	 * This draws the grayish background and lines for the Y and X axes
	 */
	public drawUI(): void {
		this.uiGraphics.clear();
		this.backgroundGraphics.clear();

		// Draw the background shape
		this.backgroundGraphics.beginFill(
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_BACKGROUND_COLOR,
			CREW_SIZE_SETTINGS.BACKGROUND_ALPHA
		);
		this.backgroundGraphics.drawRect(0, 0, this.width, this.height);
		this.backgroundGraphics.endFill();

		// Draw the X-axis background
		if (this.drawDays) {
			this.uiGraphics.beginFill(
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_BACKGROUND_COLOR,
				CREW_SIZE_SETTINGS.AXES_ALPHA
			);
			this.uiGraphics.drawRect(
				0,
				this.height / this.renderer.resolution - CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT,
				this.width,
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT
			);

			this.uiGraphics.endFill();
		}

		// Draw the Y-axis background
		if (!this._shouldTile) {
			this.uiGraphics.beginFill(CREW_SIZE_SETTINGS.TIMELINE_Y_AXIS_BACKGROUND_COLOR, 1);
			this.uiGraphics.drawRect(
				0,
				0,
				CREW_SIZE_SETTINGS.TIMELINE_Y_AXIS_WIDTH,
				this.height / this.renderer.resolution - CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT
			);
			this.uiGraphics.endFill();
		}

		// Draw the X-axis lines
		// Start at left-edge of screen if tiling mode, otherwise start with offset
		const startX: number = this._shouldTile ? 0 : CREW_SIZE_SETTINGS.TIMELINE_Y_AXIS_WIDTH;
		this.uiGraphics.lineStyle(1);
		this.uiGraphics.moveTo(
			startX,
			this.height / this.renderer.resolution -
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING
		);

		this.uiGraphics.lineTo(
			this.width,
			this.height / this.renderer.resolution -
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
				CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING
		);

		// Draw the Y-axis line
		if (!this._shouldTile) {
			this.uiGraphics.moveTo(CREW_SIZE_SETTINGS.TIMELINE_Y_AXIS_WIDTH, 0);
			this.uiGraphics.lineTo(
				CREW_SIZE_SETTINGS.TIMELINE_Y_AXIS_WIDTH,
				this.height / this.renderer.resolution -
					CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_HEIGHT -
					CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING
			);
		}

		this.uiGraphics.lineStyle(0);

		// Draw the grid
		this._drawGrid(
			CREW_SIZE_SETTINGS.TIMELINE_Y_AXIS_WIDTH,
			CREW_SIZE_SETTINGS.TIMELINE_X_AXIS_SPACING_TOP,
			CREW_SIZE_SETTINGS.GRID_SPACING
		);
	}

	private _drawGrid(startX: number, startY: number, spacing: number): void {
		this.uiGraphics.lineStyle(1, CREW_SIZE_SETTINGS.GRID_COLOR);
		// Draw max
		this.uiGraphics.moveTo(startX, startY);
		this.uiGraphics.lineTo(this.width / this.renderer.resolution, startY);

		this.uiGraphics.lineStyle(0);
	}

	public override destroy(destroyOptions?: IDestroyOptions): void {
		super.destroy(destroyOptions);

		window.removeEventListener('throttledResize', this.onResize);

		// Null the things
		this.view = null;
		this._cachedDays = null;
		this._projectLabor = null;
		this._cachedRoles = null;
		this._visibleLabor = null;
		this._lastStart = null;
		this._lastEnd = null;
		this._filterBy = null;
		this._myRoleId = null;

		this._export!.purge();
		this._export = null;
	}

	public run = () => {
		if (this.crewSizeIcon.texture.width <= 1) {
			this.crewSizeIcon.texture = utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CREW_ICON_NAME];
		}
		this.camera.update();
		// We only want horizontal lines
		this.draw();
	};

	/**
	 * Captures the provided capture area of the crew size application. This is publicly accessibly through the CrewSizeService
	 */
	public capture(captureArea: Rectangle = this.camera.view, offset?: ITileOffset): ICapturedTile {
		if (!this._export) {
			return ExportCapture.createEmptyTile();
		}
		return this._export!.capture(captureArea, this.stage, offset);
	}

	/**
	 * Enables tiling mode which modifies the crew size UI to make it "stitchable"
	 */
	public enableTilingMode(): void {
		this._shouldTile = true;
		this._midText.visible = false;
		this._maxText.visible = false;
		// Using renderable for the objects below because they have a trigger to automatically enable them at certain zoom levels
		this.crewSizeIcon.renderable = false;
		this.projectText.renderable = false;
		this.drawUI();
	}

	/**
	 * Disables tiling mode so crew sizes revert back to normal display settings
	 */
	public disableTilingMode(): void {
		this._shouldTile = false;
		this._midText.visible = true;
		this._maxText.visible = true;
		this.crewSizeIcon.renderable = true;
		this.projectText.renderable = true;
		this.drawUI();
	}

	private clear(): void {
		this.graphics.clear();
		this._planCountTextPool.reset();
		this._projectCountTextPool.reset();
		this._cachedDays = [];
		this.updateGrid(0, 1, 0);
		this.safeDraw();
	}
}
